import React, {useState} from 'react';
import {Alert, Badge, Button, Collapse, Modal, Space, Table, Tabs, Tag, Tooltip, Typography, Upload, message} from 'antd';
import {InboxOutlined, ReloadOutlined, WarningOutlined, SyncOutlined, CheckCircleOutlined, PlusCircleOutlined, MinusCircleOutlined} from '@ant-design/icons';
import {useAtom} from 'jotai';
import {RcFile} from 'antd/lib/upload';
import {batchItemsAtom, currentStepAtom} from './types';

const {Dragger} = Upload;

const {Panel} = Collapse;
const {Title, Text} = Typography;
const {TabPane} = Tabs;

interface ExcelUploadStepProps {
    excelFile: RcFile | null;
    setExcelFile: (file: RcFile | null) => void;
    uploadingExcel: boolean;
    handleExcelUpload: (isReupload: boolean) => Promise<void>;
    setReuploadModalVisible: (visible: boolean) => void;
    refreshMetadata?: () => Promise<void>;
}

const ExcelUploadStep: React.FC<ExcelUploadStepProps> = ({
                                                             excelFile,
                                                             setExcelFile,
                                                             uploadingExcel,
                                                             handleExcelUpload,
                                                             setReuploadModalVisible,
                                                             refreshMetadata
                                                         }) => {
    const [batchItems] = useAtom(batchItemsAtom);
    const [, setCurrentStep] = useAtom(currentStepAtom);

    // State for the details modal
    const [detailsModalVisible, setDetailsModalVisible] = useState(false);
    const [selectedItem, setSelectedItem] = useState<API.MusicDistribution.ReleaseBatchItem | null>(null);
    const [selectedTrack, setSelectedTrack] = useState<API.MusicDistribution.ArtTrack | null>(null);

    // Check if we have batch items (meaning an Excel file has been uploaded and parsed)
    const hasExistingData = batchItems.length > 0;

    // Function to count validation warnings
    const countValidationWarnings = (item: API.MusicDistribution.ReleaseBatchItem) => {
        let releaseWarnings = item.release?.validation_warnings?.length || 0;
        let trackWarnings = 0;

        // Count track-level warnings
        item.release?.art_tracks?.forEach(track => {
            trackWarnings += track.validation_warnings?.length || 0;
        });

        return {releaseWarnings, trackWarnings, totalWarnings: releaseWarnings + trackWarnings};
    };

    // Function to show details modal
    const showDetailsModal = (item: API.MusicDistribution.ReleaseBatchItem, track: API.MusicDistribution.ArtTrack | null = null) => {
        setSelectedItem(item);
        setSelectedTrack(track);
        setDetailsModalVisible(true);
    };

    if (!hasExistingData) {
        // If no Excel file has been uploaded yet, show the drag upload area
        return (
            <div style={{marginTop: 24}}>
                <Title level={4}>Upload Excel File</Title>
                <Text>Upload an Excel file containing track metadata. Each row should represent a track, and the system
                    will group tracks into releases based on the release title.</Text>

                <div style={{marginTop: 24}}>
                    <Dragger
                        name="file"
                        multiple={false}
                        beforeUpload={(file) => {
                            setExcelFile(file);
                            return false;
                        }}
                        onRemove={() => setExcelFile(null)}
                        fileList={(excelFile ? [excelFile] : []) as any}
                    >
                        <p className="ant-upload-drag-icon">
                            <InboxOutlined/>
                        </p>
                        <p className="ant-upload-text">Click or drag Excel file to this area to upload</p>
                        <p className="ant-upload-hint">
                            Support for a single Excel file only. The file should follow the template format.
                        </p>
                    </Dragger>
                </div>

                <div style={{marginTop: 24, textAlign: 'right'}}>
                    <Button
                        type="primary"
                        onClick={() => handleExcelUpload(false)}
                        loading={uploadingExcel}
                        disabled={!excelFile}
                    >
                        Upload and Process
                    </Button>
                </div>
            </div>
        );
    } else {
        // If an Excel file has been uploaded and parsed, show the parsed data in a table
        return (
            <div style={{marginTop: 24}}>
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16}}>
                    <div>
                        <Title level={4}>Parsed Releases and Tracks</Title>
                        <Text>The following releases and tracks have been parsed from the Excel file. You can proceed to
                            the next step or re-upload a different Excel file.</Text>
                    </div>
                    <Space>
                        <Button
                            type="primary"
                            icon={<SyncOutlined/>}
                            onClick={() => {
                                if (refreshMetadata) {
                                    message.loading('Refreshing metadata...', 1.5);
                                    refreshMetadata().then(() => {
                                        message.success('Metadata refreshed successfully');
                                    }).catch((error) => {
                                        console.error('Error refreshing metadata:', error);
                                        message.error('Failed to refresh metadata');
                                    });
                                } else {
                                    message.error('Refresh function not available');
                                }
                            }}
                        >
                            Refresh Metadata
                        </Button>
                        <Button
                            type="primary"
                            icon={<ReloadOutlined/>}
                            onClick={() => setReuploadModalVisible(true)}
                        >
                            Re-upload Excel
                        </Button>
                    </Space>
                </div>

                {/* Display any overall validation warnings */}
                {/*{batchItems.some(item =>*/}
                {/*    (item.release?.validation_warnings && item.release.validation_warnings.length > 0) ||*/}
                {/*    item.release?.art_tracks?.some(track => track.validation_warnings && track.validation_warnings.length > 0)*/}
                {/*) && (*/}
                {/*    <Alert*/}
                {/*        message="Validation Warnings"*/}
                {/*        description="There are validation warnings in the parsed data. Please review the details for each release."*/}
                {/*        type="warning"*/}
                {/*        showIcon*/}
                {/*        style={{marginBottom: 16}}*/}
                {/*    />*/}
                {/*)}*/}

                <Collapse
                    defaultActiveKey={batchItems.map(item => item.id)}
                    style={{marginTop: 24}}
                >
                    {batchItems.map((item) => {
                        const {releaseWarnings, trackWarnings, totalWarnings} = countValidationWarnings(item);

                        // Create a header with warning counts
                        const header = (
                            <Space>
                                <span>{`Release: ${item.release?.name || 'Unnamed Release'}`}</span>
                                {totalWarnings > 0 ? (
                                    <Tooltip
                                        title={`${releaseWarnings} release warnings, ${trackWarnings} track warnings`}>
                                        <Badge
                                            count={totalWarnings}
                                            style={{backgroundColor: '#faad14'}}
                                        />
                                    </Tooltip>
                                ) : (
                                    <Tooltip title="No validation warnings">
                                        <CheckCircleOutlined style={{color: '#52c41a'}} />
                                    </Tooltip>
                                )}
                                <Button
                                    type="link"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        showDetailsModal(item);
                                    }}
                                >
                                    Details
                                </Button>
                            </Space>
                        );

                        return (
                            <Panel header={header} key={item.id}>
                                {/* Release-level warnings */}
                                {releaseWarnings > 0 && (
                                    <Alert
                                        // message={
                                        //     <Space>
                                        //         <WarningOutlined/>
                                        //         <span>Release Validation Warnings</span>
                                        //         <Badge count={releaseWarnings} style={{backgroundColor: '#faad14'}}/>
                                        //     </Space>
                                        // }
                                        description={
                                            <ul>
                                                {item.release?.validation_warnings?.map((warning, index) => (
                                                    <li key={index}>
                                                        <strong>{warning.field}:</strong> {warning.message}
                                                        {warning.group !== 'general' && <span> ({warning.group})</span>}
                                                    </li>
                                                ))}
                                            </ul>
                                        }
                                        type="warning"
                                        showIcon={false}
                                        style={{marginBottom: 16}}
                                    />
                                )}

                                {/* Enhanced table with richer metadata */}
                                <Table
                                    dataSource={item.release?.art_tracks || []}
                                    columns={[
                                        {
                                            title: 'Track #',
                                            dataIndex: 'track_number',
                                            key: 'track_number',
                                            width: 80,
                                        },
                                        {
                                            title: 'CD #',
                                            dataIndex: 'volume_number',
                                            key: 'volume_number',
                                            width: 80,
                                            render: (text) => text || 1,
                                        },
                                        {
                                            title: 'Track Title',
                                            dataIndex: 'title',
                                            key: 'title',
                                            render: (text, record) => (
                                                <Space direction="vertical" size={0}>
                                                    <Text strong>{text}</Text>
                                                    {record.subtitle && <Text type="secondary">{record.subtitle}</Text>}
                                                </Space>
                                            )
                                        },
                                        {
                                            title: 'ISRC',
                                            dataIndex: 'isrc',
                                            key: 'isrc',
                                            width: 130,
                                        },
                                        {
                                            title: 'Artists',
                                            key: 'artists',
                                            render: (_, record) => {
                                                const mainArtists = record.main_artists?.map(a => a.key_name).join(', ') || '';
                                                const featArtists = record.featuring_artists?.map(a => a.key_name).join(', ') || '';

                                                return (
                                                    <Space direction="vertical" size={0}>
                                                        {mainArtists && <Text>{mainArtists}</Text>}
                                                        {featArtists &&
                                                            <Text type="secondary">feat. {featArtists}</Text>}
                                                    </Space>
                                                );
                                            }
                                        },
                                        {
                                            title: 'Validation',
                                            key: 'warnings',
                                            width: 100,
                                            render: (_, record) => {
                                                const warningCount = record.validation_warnings?.length || 0;
                                                return warningCount > 0 ? (
                                                    <Tooltip
                                                        title={`${warningCount} warning${warningCount > 1 ? 's' : ''}`}>
                                                        <Space>
                                                            <Badge count={warningCount}
                                                                   style={{backgroundColor: '#faad14'}}/>
                                                        </Space>
                                                    </Tooltip>
                                                ) : (
                                                    <Tooltip title="No validation warnings">
                                                        <CheckCircleOutlined style={{color: '#52c41a'}} />
                                                    </Tooltip>
                                                );
                                            }
                                        },
                                        {
                                            title: 'Actions',
                                            key: 'actions',
                                            width: 100,
                                            render: (_, record) => (
                                                <Button
                                                    type="link"
                                                    onClick={() => showDetailsModal(item, record)}
                                                >
                                                    Details
                                                </Button>
                                            )
                                        }
                                    ]}
                                    expandable={{
                                        expandedRowRender: record => {
                                            const warningCount = record.validation_warnings?.length || 0;
                                            if (warningCount === 0) {
                                                return <Alert message="No validation warnings for this track" type="success" showIcon />;
                                            }

                                            return (
                                                <div style={{ margin: '0 20px' }}>
                                                    {/*<Alert*/}
                                                    {/*    message={*/}
                                                    {/*        <Space>*/}
                                                    {/*            <WarningOutlined/>*/}
                                                    {/*            <span>Track Validation Warnings</span>*/}
                                                    {/*            <Badge count={warningCount} style={{backgroundColor: '#faad14'}}/>*/}
                                                    {/*        </Space>*/}
                                                    {/*    }*/}
                                                    {/*    type="warning"*/}
                                                    {/*    showIcon={false}*/}
                                                    {/*    style={{marginBottom: 8}}*/}
                                                    {/*/>*/}
                                                    <Table
                                                        dataSource={record.validation_warnings || []}
                                                        columns={[
                                                            {
                                                                title: 'Field',
                                                                dataIndex: 'field',
                                                                key: 'field',
                                                                width: '20%'
                                                            },
                                                            {
                                                                title: 'Message',
                                                                dataIndex: 'message',
                                                                key: 'message',
                                                                width: '60%'
                                                            },
                                                            {
                                                                title: 'Group',
                                                                dataIndex: 'group',
                                                                key: 'group',
                                                                width: '20%',
                                                                render: (text) => <Tag color={text === 'general' ? 'blue' : 'orange'}>{text}</Tag>
                                                            }
                                                        ]}
                                                        rowKey={(record, index) => `${record.field}-${index}`}
                                                        pagination={false}
                                                        size="small"
                                                    />
                                                </div>
                                            );
                                        },
                                        expandIcon: ({ expanded, onExpand, record }) => {
                                            const warningCount = record.validation_warnings?.length || 0;
                                            if (warningCount === 0) return null;

                                            return expanded ? (
                                                <MinusCircleOutlined 
                                                    onClick={e => onExpand(record, e)}
                                                    style={{ color: '#faad14', cursor: 'pointer' }}
                                                />
                                            ) : (
                                                <PlusCircleOutlined 
                                                    onClick={e => onExpand(record, e)}
                                                    style={{ color: '#faad14', cursor: 'pointer' }}
                                                />
                                            );
                                        },
                                        rowExpandable: record => (record.validation_warnings?.length || 0) > 0,
                                    }}
                                    rowKey="id"
                                    pagination={false}
                                    size="small"
                                />
                            </Panel>
                        );
                    })}
                </Collapse>

                <div style={{marginTop: 24, textAlign: 'right'}}>
                    <Button
                        type="primary"
                        onClick={() => setCurrentStep('upload-files')}
                    >
                        Continue to File Upload
                    </Button>
                </div>

                {/* Details Modal */}
                <Modal
                    title={selectedTrack
                        ? `Track Details: ${selectedTrack.title}`
                        : `Release Details: ${selectedItem?.release?.name || 'Unnamed Release'}`}
                    open={detailsModalVisible}
                    onCancel={() => setDetailsModalVisible(false)}
                    width={800}
                    footer={[
                        <Button key="close" onClick={() => setDetailsModalVisible(false)}>
                            Close
                        </Button>
                    ]}
                >
                    <Tabs defaultActiveKey="metadata">
                        <TabPane tab="Metadata" key="metadata">
                            {selectedTrack ? (
                                // Track metadata
                                <div>
                                    <Title level={5}>Track Information</Title>
                                    <Table
                                        dataSource={[{
                                            key: 'basic',
                                            ...selectedTrack
                                        }]}
                                        columns={[
                                            {
                                                title: 'Field',
                                                dataIndex: 'field',
                                                key: 'field',
                                                render: (_, __, index) => {
                                                    const fields = [
                                                        'Title', 'Subtitle', 'ISRC', 'Track Number',
                                                        'Volume Number', 'Duration', 'Parental Advisory',
                                                        'Is Instrumental', 'Track Type'
                                                    ];
                                                    return fields[index];
                                                }
                                            },
                                            {
                                                title: 'Value',
                                                dataIndex: 'value',
                                                key: 'value',
                                                render: (_, record, index) => {
                                                    const values = [
                                                        record.title || '-',
                                                        record.subtitle || '-',
                                                        record.isrc || '-',
                                                        record.track_number || '-',
                                                        record.volume_number || '-',
                                                        record.duration ?
                                                            `${Math.floor(record.duration / 60)}:${String(Math.floor(record.duration % 60)).padStart(2, '0')}` :
                                                            '-',
                                                        record.parental_advisory || '-',
                                                        record.is_instrumental ? 'Yes' : 'No',
                                                        record.track_type || '-'
                                                    ];
                                                    return values[index];
                                                }
                                            }
                                        ]}
                                        pagination={false}
                                        showHeader={true}
                                        size="small"
                                    />

                                    <Title level={5} style={{marginTop: 16}}>Artists</Title>
                                    <Table
                                        dataSource={[
                                            ...(selectedTrack.main_artists || []).map(artist => ({
                                                ...artist,
                                                type: 'Main Artist',
                                                key: `main-${artist.key_name}`
                                            })),
                                            ...(selectedTrack.featuring_artists || []).map(artist => ({
                                                ...artist,
                                                type: 'Featuring Artist',
                                                key: `feat-${artist.key_name}`
                                            }))
                                        ]}
                                        columns={[
                                            {
                                                title: 'Artist Name',
                                                dataIndex: 'key_name',
                                                key: 'key_name'
                                            },
                                            {
                                                title: 'Role',
                                                dataIndex: 'type',
                                                key: 'type'
                                            },
                                            {
                                                title: 'Spotify ID',
                                                dataIndex: 'spotify_id',
                                                key: 'spotify_id',
                                                render: (text) => text || '-'
                                            },
                                            {
                                                title: 'Apple Music ID',
                                                dataIndex: 'apple_music_id',
                                                key: 'apple_music_id',
                                                render: (text) => text || '-'
                                            }
                                        ]}
                                        pagination={false}
                                        size="small"
                                    />
                                </div>
                            ) : (
                                // Release metadata
                                <div>
                                    <Title level={5}>Release Information</Title>
                                    <Table
                                        dataSource={[{
                                            key: 'basic',
                                            ...selectedItem?.release
                                        }]}
                                        columns={[
                                            {
                                                title: 'Field',
                                                dataIndex: 'field',
                                                key: 'field',
                                                render: (_, __, index) => {
                                                    const fields = [
                                                        'Title', 'Subtitle', 'UPC/EAN', 'Type',
                                                        'Genre', 'Sub-genre', 'Digital Release Date',
                                                        'Original Release Date', 'Production Year',
                                                        'Price Tier', 'Territories'
                                                    ];
                                                    return fields[index];
                                                }
                                            },
                                            {
                                                title: 'Value',
                                                dataIndex: 'value',
                                                key: 'value',
                                                render: (_, record, index) => {
                                                    const values = [
                                                        record.name || '-',
                                                        record.subtitle || '-',
                                                        record.upc_ean || '-',
                                                        record.type || '-',
                                                        record.genre || '-',
                                                        record.sub_genre || '-',
                                                        record.digital_release_date || '-',
                                                        record.original_release_date || '-',
                                                        record.production_year || '-',
                                                        record.price_tier || '-',
                                                        Array.isArray(record.territories)
                                                            ? record.territories.join(', ')
                                                            : (record.territories || '-')
                                                    ];
                                                    return values[index];
                                                }
                                            }
                                        ]}
                                        pagination={false}
                                        showHeader={true}
                                        size="small"
                                    />

                                    <Title level={5} style={{marginTop: 16}}>Label Information</Title>
                                    <Table
                                        dataSource={[{
                                            key: 'label',
                                            label: selectedItem?.release?.label?.name || '-',
                                            p_line: selectedItem?.release?.label?.p_line || '-',
                                            c_line: selectedItem?.release?.label?.c_line || '-',
                                            asset_label: selectedItem?.release?.asset_label?.name || '-'
                                        }]}
                                        columns={[
                                            {
                                                title: 'Label',
                                                dataIndex: 'label',
                                                key: 'label'
                                            },
                                            {
                                                title: 'P-Line',
                                                dataIndex: 'p_line',
                                                key: 'p_line'
                                            },
                                            {
                                                title: 'C-Line',
                                                dataIndex: 'c_line',
                                                key: 'c_line'
                                            },
                                            {
                                                title: 'Asset Label',
                                                dataIndex: 'asset_label',
                                                key: 'asset_label'
                                            }
                                        ]}
                                        pagination={false}
                                        size="small"
                                    />

                                    <Title level={5} style={{marginTop: 16}}>Artists</Title>
                                    <Table
                                        dataSource={[
                                            ...(selectedItem?.release?.artists || []).map(artist => ({
                                                ...artist,
                                                type: 'Main Artist',
                                                key: `main-${artist.key_name}`
                                            })),
                                            ...(selectedItem?.release?.featuring_artists || []).map(artist => ({
                                                ...artist,
                                                type: 'Featuring Artist',
                                                key: `feat-${artist.key_name}`
                                            }))
                                        ]}
                                        columns={[
                                            {
                                                title: 'Artist Name',
                                                dataIndex: 'key_name',
                                                key: 'key_name'
                                            },
                                            {
                                                title: 'Role',
                                                dataIndex: 'type',
                                                key: 'type'
                                            },
                                            {
                                                title: 'Spotify ID',
                                                dataIndex: 'spotify_id',
                                                key: 'spotify_id',
                                                render: (text) => text || '-'
                                            },
                                            {
                                                title: 'Apple Music ID',
                                                dataIndex: 'apple_music_id',
                                                key: 'apple_music_id',
                                                render: (text) => text || '-'
                                            }
                                        ]}
                                        pagination={false}
                                        size="small"
                                    />
                                </div>
                            )}
                        </TabPane>

                        <TabPane tab="Validation Warnings" key="warnings">
                            {selectedTrack ? (
                                // Track validation warnings
                                <div>
                                    {selectedTrack.validation_warnings && selectedTrack.validation_warnings.length > 0 ? (
                                        <>
                                            <Alert
                                                message={
                                                    <Space>
                                                        <WarningOutlined/>
                                                        <span>Track Validation Warnings</span>
                                                        <Badge count={selectedTrack.validation_warnings.length}
                                                               style={{backgroundColor: '#faad14'}}/>
                                                    </Space>
                                                }
                                                type="warning"
                                                showIcon={false}
                                                style={{marginBottom: 16}}
                                            />
                                            <Table
                                                dataSource={selectedTrack.validation_warnings}
                                                columns={[
                                                    {
                                                        title: 'Field',
                                                        dataIndex: 'field',
                                                        key: 'field'
                                                    },
                                                    {
                                                        title: 'Message',
                                                        dataIndex: 'message',
                                                        key: 'message'
                                                    },
                                                    {
                                                        title: 'Group',
                                                        dataIndex: 'group',
                                                        key: 'group',
                                                        render: (text) => <Tag
                                                            color={text === 'general' ? 'blue' : 'orange'}>{text}</Tag>
                                                    }
                                                ]}
                                                rowKey={(record, index) => `${record.field}-${index}`}
                                                pagination={false}
                                                size="small"
                                            />
                                        </>
                                    ) : (
                                        <Alert message="No validation warnings for this track" type="success" showIcon/>
                                    )}
                                </div>
                            ) : (
                                // Release validation warnings
                                <div>
                                    {selectedItem?.release?.validation_warnings && selectedItem.release.validation_warnings.length > 0 ? (
                                        <>
                                            <Alert
                                                message={
                                                    <Space>
                                                        <WarningOutlined/>
                                                        <span>Release Validation Warnings</span>
                                                        <Badge count={selectedItem.release.validation_warnings.length}
                                                               style={{backgroundColor: '#faad14'}}/>
                                                    </Space>
                                                }
                                                type="warning"
                                                showIcon={false}
                                                style={{marginBottom: 16}}
                                            />
                                            <Table
                                                dataSource={selectedItem.release.validation_warnings}
                                                columns={[
                                                    {
                                                        title: 'Field',
                                                        dataIndex: 'field',
                                                        key: 'field'
                                                    },
                                                    {
                                                        title: 'Message',
                                                        dataIndex: 'message',
                                                        key: 'message'
                                                    },
                                                    {
                                                        title: 'Group',
                                                        dataIndex: 'group',
                                                        key: 'group',
                                                        render: (text) => <Tag
                                                            color={text === 'general' ? 'blue' : 'orange'}>{text}</Tag>
                                                    }
                                                ]}
                                                rowKey={(record, index) => `${record.field}-${index}`}
                                                pagination={false}
                                                size="small"
                                            />
                                        </>
                                    ) : (
                                        <Alert message="No validation warnings for this release" type="success"
                                               showIcon/>
                                    )}

                                    {/* Track-level warnings summary */}
                                    <Title level={5} style={{marginTop: 16}}>Track-level Warnings</Title>
                                    {selectedItem?.release?.art_tracks?.some(track => track.validation_warnings && track.validation_warnings.length > 0) ? (
                                        <Table
                                            dataSource={selectedItem.release.art_tracks.filter(track =>
                                                track.validation_warnings && track.validation_warnings.length > 0
                                            )}
                                            columns={[
                                                {
                                                    title: 'Track',
                                                    dataIndex: 'title',
                                                    key: 'title'
                                                },
                                                {
                                                    title: 'Warnings',
                                                    key: 'warnings',
                                                    render: (_, record) => (
                                                        <Badge
                                                            count={record.validation_warnings?.length || 0}
                                                            style={{backgroundColor: '#faad14'}}
                                                        />
                                                    )
                                                },
                                                {
                                                    title: 'Actions',
                                                    key: 'actions',
                                                    render: (_, record) => (
                                                        <Button
                                                            type="link"
                                                            onClick={() => {
                                                                setSelectedTrack(record);
                                                            }}
                                                        >
                                                            View Details
                                                        </Button>
                                                    )
                                                }
                                            ]}
                                            rowKey="id"
                                            pagination={false}
                                            size="small"
                                        />
                                    ) : (
                                        <Alert message="No validation warnings for any tracks in this release"
                                               type="success" showIcon/>
                                    )}
                                </div>
                            )}
                        </TabPane>
                    </Tabs>
                </Modal>
            </div>
        );
    }
};

export default ExcelUploadStep;
